import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Camera, FileUp, X, Check, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Bill / Voucher capture field:
 *   - Upload (PDF / JPG / JPEG / PNG)
 *   - Live camera capture (uses getUserMedia)
 *   - Auto-shrinks images to max 1600px on the long edge at JPEG quality 0.8
 *   - Passes { dataUrl, filename } upward via onChange
 *
 * Props:
 *   value:    { dataUrl, filename } | null
 *   onChange: (next) => void
 *   disabled: boolean
 */
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.8;

const shrinkImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      let w = img.width;
      let h = img.height;
      if (w > MAX_EDGE || h > MAX_EDGE) {
        if (w >= h) { h = Math.round(h * (MAX_EDGE / w)); w = MAX_EDGE; }
        else { w = Math.round(w * (MAX_EDGE / h)); h = MAX_EDGE; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };
    img.onerror = reject;
    img.src = e.target.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const readAsDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = (e) => resolve(e.target.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const BillCaptureField = ({ value, onChange, disabled }) => {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [mode, setMode] = useState('upload'); // upload | camera
  const [cameraActive, setCameraActive] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Stop camera on unmount / when mode changes
  useEffect(() => {
    return () => stopCamera();
    // eslint-disable-next-line
  }, []);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraActive(true);
    } catch (err) {
      toast.error('Camera unavailable — please allow permission or use Upload instead');
    }
  };

  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    let w = video.videoWidth;
    let h = video.videoHeight;
    if (w > MAX_EDGE || h > MAX_EDGE) {
      if (w >= h) { h = Math.round(h * (MAX_EDGE / w)); w = MAX_EDGE; }
      else { w = Math.round(w * (MAX_EDGE / h)); h = MAX_EDGE; }
    }
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    const filename = `bill-capture-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}.jpg`;
    onChange({ dataUrl, filename });
    stopCamera();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = /(pdf|jpe?g|png)$/i;
    const ext = file.name.split('.').pop();
    if (!allowed.test(ext) && !/(image\/(jpeg|jpg|png)|application\/pdf)/.test(file.type)) {
      toast.error('Only PDF, JPG, JPEG or PNG files are allowed');
      e.target.value = '';
      return;
    }

    setProcessing(true);
    try {
      let dataUrl;
      if (file.type.startsWith('image/')) {
        dataUrl = await shrinkImage(file);
      } else {
        // PDF or unknown — pass through
        if (file.size > 10 * 1024 * 1024) {
          toast.error('PDF larger than 10 MB — please compress it first');
          setProcessing(false);
          e.target.value = '';
          return;
        }
        dataUrl = await readAsDataURL(file);
      }
      onChange({ dataUrl, filename: file.name });
    } catch {
      toast.error('Failed to read the file');
    } finally {
      setProcessing(false);
      e.target.value = '';
    }
  };

  const clear = () => onChange(null);

  return (
    <div className="space-y-2" data-testid="bill-capture-field">
      <Label>Bill / Voucher <span className="text-xs text-slate-500">(optional, PDF / JPG / PNG — auto-compressed)</span></Label>

      {/* Existing preview */}
      {value?.dataUrl && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          {value.dataUrl.startsWith('data:image/') ? (
            <img src={value.dataUrl} alt="Bill preview" className="h-14 w-14 object-cover rounded" data-testid="bill-preview-thumb" />
          ) : (
            <div className="h-14 w-14 bg-slate-200 rounded flex items-center justify-center text-slate-600 text-xs font-mono">PDF</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-800 truncate">{value.filename || 'attachment'}</div>
            <div className="text-xs text-slate-500">Attached ✓ — this will replace the existing bill (if any)</div>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={clear} disabled={disabled} className="text-red-600 hover:bg-red-50" data-testid="clear-bill">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Mode toggle */}
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant={mode === 'upload' ? 'default' : 'outline'}
          onClick={() => { setMode('upload'); stopCamera(); }}
          disabled={disabled}
          className={mode === 'upload' ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : ''}
          data-testid="bill-mode-upload"
        >
          <FileUp className="w-4 h-4 mr-1" /> Upload
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === 'camera' ? 'default' : 'outline'}
          onClick={() => setMode('camera')}
          disabled={disabled}
          className={mode === 'camera' ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : ''}
          data-testid="bill-mode-camera"
        >
          <Camera className="w-4 h-4 mr-1" /> Camera
        </Button>
      </div>

      {mode === 'upload' && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,image/jpeg,image/png,application/pdf"
            onChange={handleFile}
            disabled={disabled || processing}
            className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
            data-testid="bill-file-input"
          />
          {processing && <p className="text-xs text-slate-500 mt-1">Compressing…</p>}
        </div>
      )}

      {mode === 'camera' && (
        <div className="border border-slate-200 rounded-lg p-2 bg-slate-50">
          {!cameraActive ? (
            <Button type="button" onClick={startCamera} disabled={disabled} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="bill-camera-start">
              <Camera className="w-4 h-4 mr-1" /> Start Camera
            </Button>
          ) : (
            <div className="space-y-2">
              <video ref={videoRef} autoPlay playsInline className="w-full rounded bg-black max-h-64 object-contain" data-testid="bill-camera-video" />
              <div className="flex gap-2">
                <Button type="button" onClick={captureFrame} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="bill-camera-capture">
                  <Check className="w-4 h-4 mr-1" /> Capture
                </Button>
                <Button type="button" variant="outline" onClick={stopCamera}>
                  <RotateCw className="w-4 h-4 mr-1" /> Retake later
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default BillCaptureField;
